import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DoneIcon from "@mui/icons-material/Done";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import {
  Box,
  Button,
  IconButton,
  Paper,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "transparent",
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: "0px 3px 6px #0000001F",
    zIndex: "1",
  },
}));

const DescTooltip = ({ children, data, placement = "right" }) => {
  return (
    <LightTooltip
      title={
        <div
          style={{
            backgroundColor: "#fff",
            color: "#000",
            margin: "-5px -8px",
            boxShadow: "none",
            borderRadius: "5px",
            fontWeight: "600",
            padding: "4px 6px",
          }}
        >
          {data}
        </div>
      }
      placement={placement}
    >
      {children}
    </LightTooltip>
  );
};

const Filter_Data = {
  display: "block",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  columnGap: "8px",
  padding: "4px 12px",
};

function FilterTableHeader({
  headers = [],
  data = [],
  tableData = [],
  setTableData = () => {},
  handleSize = () => {},
}) {
  const wrapperRef = useRef(null);

  const [activeHeader, setActiveHeader] = useState(null);
  const [filters, setFilters] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [order, setOrder] = useState(null);

  // Handling select all for dropdown menus
  // Checking if filter exist for the column or not, If filter exist then it'll remove every entry and reset it else it'll create all entries, Using set to make sure every data is distinct
  const handleFilterSelectAll = useCallback(
    (column, data) => {
      if (filters?.[column]) {
        setFilters({
          ...filters,
          [column]:
            data?.length === filters?.[column]?.length
              ? []
              : [...new Set([...data, ...filters?.[column]])],
        });
      } else {
        setFilters({
          ...filters,
          [column]: [...data],
        });
      }
    },
    [filters]
  );

  // Handling filter change event, Applying filter to raw data and setting it to table data
  const handleFilterChange = useCallback(
    (column, values) => {
      if (filters?.[column]) {
        let data = filters?.[column]?.includes(values)
          ? filters?.[column]?.filter((i) => i !== values)
          : [...filters?.[column], values];
        setFilters({
          ...filters,
          [column]: data,
        });
      } else {
        setFilters({
          ...filters,
          [column]: [values],
        });
      }
    },
    [filters]
  );

  // Applying filter
  const filterData = useCallback(
    (filters = {}) => {
      // console.log(filters);
      let filteredData = data;
      // Apply filters to each column
      Object.keys(filters).forEach((column) => {
        const filterValues = filters[column];
        if (filterValues && filterValues.length > 0) {
          filteredData = filteredData.filter((row) =>
            filterValues.includes(row[column])
          );
        }
      });
      setTableData(filteredData);
      setActiveHeader(null);
    },
    [data, setTableData]
  );

  // Apply sorting
  const sortData = useCallback((col, order) => {
    // setSortColumn(col);
    // setOrder(order);
    // setTableData((p) => [
    //   ...p.sort((a, b) =>
    //     order === "asc" ? a[col] - b[col] : b[col] - a[col]
    //   ),
    // ]);
    // setActiveHeader(null);
    setSortColumn(col);
    setOrder(order);
    setTableData((prevData) => {
      const sortedData = [...prevData].sort((a, b) => {
        // Check if the values are dates (assuming date format "YYYY-MM-DD")
        if (
          /^\d{4}-\d{2}-\d{2}$/.test(a[col]) &&
          /^\d{4}-\d{2}-\d{2}$/.test(b[col])
        ) {
          // Convert date strings to Date objects
          const dateA = new Date(a[col]);
          const dateB = new Date(b[col]);
          return order === "asc" ? dateA - dateB : dateB - dateA;
        } else {
          // Handle non-date data types: numbers and strings
          if (typeof a[col] === "number" && typeof b[col] === "number") {
            return order === "asc" ? a[col] - b[col] : b[col] - a[col];
          } else {
            // Fallback to localeCompare for string comparison
            return order === "asc"
              ? a[col].localeCompare(b[col])
              : b[col].localeCompare(a[col]);
          }
        }
      });
      return sortedData;
    });
    setActiveHeader(null);
  }, []);

  // clearing filter
  const handleCancelFilter = useCallback(
    (column) => {
      let filter = {
        ...filters,
        [column]: [],
      };
      setFilters(filter);
      setActiveHeader(null);
      filterData(filter);
      setOrder(null);
      setSortColumn(null);
      handleSize(false);

      // setTableData(data);
    },
    [filterData, filters, data?.length]
  );

  useEffect(() => {
    setFilters({});
    setActiveHeader(null);
    setOrder(null);
    setSortColumn(null);
    handleSize(false);
  }, [data?.length]);

  useEffect(() => {
    // Function to handle click events
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        // Click occurred outside of the wrapperRef, so call the callback
        setActiveHeader(null);
        handleSize(false);
      }
    };

    // Add event listener when the component mounts
    document.addEventListener("mousedown", handleClickOutside);

    // Remove event listener when the component unmounts
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <TableHead>
      <TableRow ref={wrapperRef}>
        {headers.map((i, n) => (
          <TableCell
            key={n}
            sx={{
              backgroundColor: "#D9D9D9",
              border: "1px solid #e1e1e1",
              // whiteSpace: "nowrap",
              padding: 0,
            }}
          >
            <Box
              sx={{
                width: "100%",
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                // justifyContent: "space-between",
                position: "relative",
                // flexDirection: "column",
                textAlign: "center",
              }}
            >
              {i?.label ? (
                <DescTooltip data={i?.label} placement="top">
                  <Typography fontSize={12} fontWeight={600}>
                    {i.title}
                  </Typography>
                </DescTooltip>
              ) : (
                <Typography fontSize={12} fontWeight={600}>
                  {i.title}
                </Typography>
              )}

              {(i?.search || i?.sort) && (
                <>
                  {!!i.value && (
                    <IconButton
                      onClick={() => {
                        setActiveHeader((p) => {
                          handleSize(p === i.value ? false : true);
                          return p === i.value ? null : i.value;
                        });
                        // handleSize((prev) => !prev);
                      }}
                    >
                      <FilterAltIcon
                        sx={{
                          fontSize: 14,
                          color:
                            filters?.[i?.value]?.length > 0 ||
                            sortColumn === i?.value
                              ? "red"
                              : activeHeader === i.value
                              ? "blue"
                              : "grey",
                        }}
                      />
                    </IconButton>
                  )}
                </>
              )}

              {activeHeader === i.value && (
                <Box
                  component={Paper}
                  sx={{
                    position: "absolute",
                    width: "100%",
                    minWidth: "180px",
                    maxHeight: "180px",
                    left: 0,
                    top: "100%",
                    overflowY: "scroll",
                    zIndex: 98,
                  }}
                  className="Selection-Filters"
                >
                  {i?.sort && (
                    <>
                      <button
                        size="small"
                        style={{
                          textTransform: "initial",
                          margin: "6px",
                          width: "calc(100% - 12px)",
                          textAlign: "left",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          backgroundColor:
                            sortColumn === i.value && order === "asc"
                              ? "rgba(0,0,0,0.1)"
                              : "white",
                          cursor: "pointer",
                          padding: "6px",
                          outline: "none",
                          border: "none",
                          fontWeight: "500",
                        }}
                        onClick={() => {
                          sortData(i?.value, "asc");
                        }}
                      >
                        <ArrowUpwardIcon
                          sx={{
                            fontSize: 16,
                          }}
                        />
                        Sort Ascending
                      </button>
                      <button
                        size="small"
                        style={{
                          textTransform: "initial",
                          margin: "6px",
                          width: "calc(100% - 12px)",
                          textAlign: "left",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          backgroundColor:
                            sortColumn === i.value && order === "dsc"
                              ? "rgba(0,0,0,0.1)"
                              : "white",
                          cursor: "pointer",
                          padding: "6px",
                          outline: "none",
                          border: "none",
                          fontWeight: "500",
                        }}
                        onClick={() => {
                          sortData(i?.value, "dsc");
                        }}
                      >
                        <ArrowDownwardIcon
                          sx={{
                            fontSize: 16,
                          }}
                        />
                        Sort Descending
                      </button>
                    </>
                  )}
                  {i?.search && (
                    <>
                      <Box
                        style={Filter_Data}
                        onClick={(e) =>
                          handleFilterSelectAll(i.value, [
                            ...new Set(data.map((data) => data?.[i.value])),
                          ])
                        }
                      >
                        <Box
                          sx={{
                            border: `2px solid blue`,
                            width: "16px",
                            height: "16px",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor:
                              filters?.[i?.value]?.length ===
                                [...new Set(data.map((d) => d?.[i.value]))]
                                  .length && "blue",
                          }}
                        >
                          {filters?.[i?.value]?.length ===
                            [...new Set(data.map((d) => d?.[i.value]))]
                              .length && (
                            <DoneIcon
                              sx={{
                                fontSize: "14px",
                                color: "white",
                              }}
                            />
                          )}
                        </Box>
                        Select All
                      </Box>
                      {[...new Set(data.map((d) => d?.[i.value]))].map((d) => (
                        <Box
                          style={Filter_Data}
                          onClick={(e) => handleFilterChange(i.value, d)}
                        >
                          <Box
                            sx={{
                              border: `2px solid blue`,
                              width: "16px",
                              height: "16px",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor:
                                filters?.[i?.value]?.includes(d) && "blue",
                            }}
                          >
                            {filters?.[i?.value]?.includes(d) && "red" && (
                              <DoneIcon
                                sx={{
                                  fontSize: "14px",
                                  color: "white",
                                }}
                              />
                            )}
                          </Box>
                          {d}
                        </Box>
                      ))}
                    </>
                  )}
                  {(i?.search || i?.sort) && (
                    <Box
                      sx={{
                        position: "sticky",
                        bottom: "0%",
                        width: "100%",
                        height: "32px",
                        backgroundColor: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-evenly",
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          fontSize: "8px",
                        }}
                        onClick={() => filterData(filters)}
                      >
                        Apply
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "8px",
                        }}
                        onClick={() => handleCancelFilter(i.value)}
                      >
                        Clear
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default FilterTableHeader;
